import React, { useState, useEffect, useContext, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

// Openlayers
import { toStringHDMS as OlToStringHDMS } from 'ol/coordinate';
import { transform } from 'ol/proj';
import { MapEvent } from 'ol';

// Material UI Core Elements
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTheme, useMediaQuery } from '@mui/material';

// Material UI Icons
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import CenterFocusStrongIcon from '@mui/icons-material/CenterFocusStrong';

// Custom Components
import CoordZoomStatusRoot from '@/components/Map/Controls/CoordZoomStatus/CoordZoomStatusRoot';

// Context
import MapContext from '@/components/Map/MapContext';

// Types
import { ICordZoomStatusControl } from '@/@types/components/Map/Controls/CordZoomStatus';
import { MapContextType } from '@/@types/context/MapContext';

const CoordZoomStatusControl: FunctionComponent<ICordZoomStatusControl> = (props) => {
  const mapContext = useContext(MapContext) as MapContextType;

  const { t } = useTranslation();

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const [center, setCenter] = useState<number[] | undefined>(undefined);
  const [zoom, setZoom] = useState<number | undefined>(undefined);

  const { chosenCoordinateSystem, isPublic } = props;

  useEffect(() => {
    if (mapContext.map) {
      mapContext.map.on('moveend', handleMoveEnd);
    }

    return () => {
      if (mapContext.map) {
        mapContext.map.un('moveend', handleMoveEnd);
      }
    };
  }, [mapContext.map]);

  const handleMoveEnd = (evt: MapEvent) => {
    const view = evt.map.getView();
    if (view) {
      setCenter(view.getCenter());
      setZoom(view.getZoom());
    }
  };

  const displayCoords = (type: 'HTRS96/TM' | 'WGS84'): string => {
    if (center) {
      switch (type) {
        case 'HTRS96/TM':
          const htrs_array = transform(center, 'EPSG:3857', 'EPSG:3765');
          return mdUp
            ? `${htrs_array[0].toFixed(2)}, ${htrs_array[1].toFixed(2)}`
            : `${htrs_array[0].toFixed(2)}\n${htrs_array[1].toFixed(2)}`;
        case 'WGS84':
          const wgs_string = OlToStringHDMS(transform(center, 'EPSG:3857', 'EPSG:4326'), 2);
          const wg_string_split = wgs_string.split(' ');
          return mdUp
            ? OlToStringHDMS(transform(center, 'EPSG:3857', 'EPSG:4326'), 2)
            : `${wg_string_split.slice(0, 4).join(' ')}\n${wg_string_split.slice(4, 8).join(' ')}`;
        default:
          return '';
      }
    } else {
      return '';
    }
  };

  const copyCoordinates = (): void => {
    navigator.clipboard.writeText(displayCoords(chosenCoordinateSystem) as string);
  };

  const textCenter = isPublic
    ? `${t('coordzoomstatus.center')}: ${displayCoords(chosenCoordinateSystem)} `
    : `${displayCoords(chosenCoordinateSystem)} `;
  const textZoom = isPublic
    ? `${t('coordzoomstatus.zoom')}: ${zoom ? zoom.toFixed(1).toString() : ''}`
    : `${zoom ? zoom.toFixed(1).toString() : ''}`;

  return (
    // @ts-ignore TODO: ref is missing in type ICordZoomStatusRoot, probably need to pass down ref
    <CoordZoomStatusRoot isPublic={isPublic}>
      {isPublic ? (
        <Box m={0} style={{ paddingLeft: 5 }}>
          <Typography variant="caption">
            {textCenter}
            {textZoom}
          </Typography>
        </Box>
      ) : (
        <Box m={0} sx={{ gap: '4px', display: 'flex', alignItmes: 'center', height: '100%', py: '1px' }}>
          <Tooltip title={t('coordzoomstatus.copy') as string}>
            <Box onClick={copyCoordinates} display="flex" alignItems="center" gap="2px" sx={{ cursor: 'pointer' }}>
              <CenterFocusStrongIcon fontSize="small" sx={{ transform: 'scale(0.85)' }} />
              <Typography variant="caption" lineHeight={1.1} whiteSpace="pre-line">
                {textCenter}
              </Typography>
            </Box>
          </Tooltip>
          <Box display="flex" alignItems="center" gap="1px">
            <ZoomInIcon fontSize="small" />
            <Typography variant="caption" lineHeight={1.1} whiteSpace="pre-line">
              {textZoom}
            </Typography>
          </Box>
        </Box>
      )}
    </CoordZoomStatusRoot>
  );
};

export default CoordZoomStatusControl;
